import { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { SelectTimezone } from '@capaj/react-select-timezone'
import { Button } from '@context365/button'
import { DatePicker, Input, Select, TimePicker } from '@context365/forms'
import { Save } from '@context365/icons'
import { Modal } from '@context365/modals/Modal'
import { Select as MultiSelect, message } from 'antd'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import map from 'lodash/map'
import moment from 'moment'
import { useHistory } from 'react-router-dom'
import {
  checkForDupeCompanies,
  createCompany,
  getTierOptions,
} from '../../actions/company'
import { createEvent } from '../../actions/events'
import { getCountries } from '../../actions/fundList'
import {
  CATEGORY_ALLOCATOR,
  CATEGORY_MANAGER,
  CATEGORY_SERVICE_PROVIDER,
} from '../../constants/types/categories'
import http from '../../utils/api'

const NewEventForm = () => {
  const [event, setEvent] = useState({
    meetingDurationInMinutes: 30,
  })

  const history = useHistory()

  const [countries, setCountries] = useState([])
  const [touched, setTouched] = useState(false)
  const [meetingPeriods, setMeetingPeriods] = useState([])

  useEffect(() => {
    getCountries().then((res) => setCountries(res.data))
  }, [])

  const handleChange = useCallback(
    (key, value) => {
      setEvent({ ...event, [key]: value })
    },
    [event]
  )

  const handleSave = () => {
    if (
      isEmpty(event.name) ||
      isNil(event.timeZone) ||
      isNil(event.startDate) ||
      isNil(event.endDate) ||
      isNil(event.schedulerAccessDate)
    ) {
      setTouched(true)
      return
    } else if (new Date(event.startDate) > new Date(event.endDate)) {
      message.error('Event end date must be after start date')
      return
    } else if (
      new Date(event.schedulerAccessDate) > new Date(event.startDate)
    ) {
      message.error('Event start date must be after scheduler access date')
      return
    } else {
      setTouched(false)

      const formattedMeetingPeriods = meetingPeriods.map((row) => ({
        date: row.date
          ? moment.tz(row.date, event.timeZone).utc().format('YYYY-MM-DD')
          : null,
        earliestMeetingStartTime: row.earliestMeetingStartTime
          ? moment
              .tz(row.earliestMeetingStartTime, event.timeZone)
              .utc()
              .format('HH:mm:ss')
          : null,
        latestMeetingEndTime: row.latestMeetingEndTime
          ? moment
              .tz(row.latestMeetingEndTime, event.timeZone)
              .utc()
              .format('HH:mm:ss')
          : null,
      }))

      const dataToSend = {
        ...event,
        startDate: event.startDate
          ? moment
              .tz(event.startDate, event.timeZone)
              .utc()
              .format('YYYY-MM-DDTHH:mm:ss')
          : null,
        endDate: event.endDate
          ? moment
              .tz(event.endDate, event.timeZone)
              .utc()
              .format('YYYY-MM-DDTHH:mm:ss')
          : null,
        schedulerAccessDate: event.schedulerAccessDate
          ? moment
              .tz(event.schedulerAccessDate, event.timeZone)
              .utc()
              .format('YYYY-MM-DDTHH:mm:ss')
          : null,
        meetingPeriods: formattedMeetingPeriods,
      }

      createEvent(dataToSend)
        .then(() => {
          message.success('Event created successfully')
          history.push('/summits')
        })
        .catch(() =>
          message.error('An error ocurred while creating this event')
        )
    }
  }

  const addScheduleRow = () => {
    setMeetingPeriods([
      ...meetingPeriods,
      { date: '', earliestMeetingStartTime: '', latestMeetingEndTime: '' },
    ])
  }

  const updateScheduleRow = (index, key, value) => {
    const updated = [...meetingPeriods]
    updated[index][key] = value
    setMeetingPeriods(updated)
  }

  const removeScheduleRow = (index) => {
    const updated = [...meetingPeriods]
    updated.splice(index, 1)
    setMeetingPeriods(updated)
  }

  return (
    <>
      <div className="bg-white p-4">
        <div className="type-header-md mb-4">Create New Event</div>
        <div className="grid grid-cols-4 gap-4">
          <Input
            className="col-span-2"
            required
            label="Event Name"
            value={event.name}
            errorMessage={touched && isEmpty(event.name) ? 'Required' : null}
            onChange={(e) => handleChange('name', e.target.value)}
          />
          <div className="col-start-1">
            <label className="block text-sm font-medium text-gray-700">
              Event Time Zone<span style={{ color: '#d62b34' }}>*</span>
            </label>
            <SelectTimezone
              label="Event Time Zone"
              value={event.timeZone}
              onChange={(d) => handleChange('timeZone', d)}
            />
            {touched && isEmpty(event.timeZone) && (
              <div className="form-message form-message--has-error">
                Required
              </div>
            )}
          </div>
          <DatePicker
            className="col-start-1"
            label="Start Date"
            required
            value={event.startDate ? new Date(event.startDate) : null}
            errorMessage={
              touched && isEmpty(event.startDate) ? 'Required' : null
            }
            onChange={(d) => handleChange('startDate', d.toDateString())}
          />
          <TimePicker
            label="Start Time (Event Time Zone)"
            required
            value={event.startDate ? new Date(event.startDate) : null}
            forDate={event.startDate ? new Date(event.startDate) : null}
            twelveHour={true}
            errorMessage={touched && isNil(event.startDate) ? 'Required' : null}
            onChange={(d) => handleChange('startDate', d)}
          />
          <DatePicker
            className="col-start-1"
            label="End Date"
            required
            value={event.endDate ? new Date(event.endDate) : null}
            errorMessage={touched && isEmpty(event.endDate) ? 'Required' : null}
            onChange={(d) => handleChange('endDate', d.toDateString())}
          />
          <TimePicker
            required
            label="End Time (Event Time Zone)"
            value={event.endDate ? new Date(event.endDate) : null}
            forDate={event.endDate ? new Date(event.endDate) : null}
            twelveHour={true}
            errorMessage={touched && isNil(event.endDate) ? 'Required' : null}
            onChange={(d) => handleChange('endDate', d)}
          />
          <DatePicker
            className="col-start-1"
            label="Scheduler Access Date"
            required
            value={
              event.schedulerAccessDate
                ? new Date(event.schedulerAccessDate)
                : null
            }
            errorMessage={
              touched && isEmpty(event.schedulerAccessDate) ? 'Required' : null
            }
            onChange={(d) =>
              handleChange('schedulerAccessDate', d.toDateString())
            }
          />
          <TimePicker
            required
            label="Scheduler Access Time (Event Time Zone)"
            value={
              event.schedulerAccessDate
                ? new Date(event.schedulerAccessDate)
                : null
            }
            forDate={
              event.schedulerAccessDate
                ? new Date(event.schedulerAccessDate)
                : null
            }
            twelveHour={true}
            errorMessage={
              touched && isNil(event.schedulerAccessDate) ? 'Required' : null
            }
            onChange={(d) => handleChange('schedulerAccessDate', d)}
          />
          <Input label="Meeting Duration (minutes)" value={30} disabled />
          <div className="col-span-4 mt-6">
            <div className="text-lg font-semibold mb-2">Meeting Periods</div>
            {meetingPeriods.length == 0 && (
              <Button
                className="mt-2"
                onClick={addScheduleRow}
                variant="outline"
              >
                + Add Meeting Period
              </Button>
            )}
            {meetingPeriods.length > 0 && (
              <>
                {meetingPeriods.map((row, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-4 gap-4 items-end mb-2"
                  >
                    <DatePicker
                      required
                      label="Date"
                      value={row.date ? new Date(row.date) : null}
                      onChange={(d) =>
                        updateScheduleRow(index, 'date', d.toDateString())
                      }
                      errorMessage={
                        touched && isEmpty(row.date) ? 'Required' : null
                      }
                    />
                    <TimePicker
                      required
                      label="Start Time"
                      value={
                        row.earliestMeetingStartTime
                          ? new Date(row.earliestMeetingStartTime)
                          : null
                      }
                      onChange={(d) =>
                        updateScheduleRow(index, 'earliestMeetingStartTime', d)
                      }
                      errorMessage={
                        touched && isEmpty(row.earliestMeetingStartTime)
                          ? 'Required'
                          : null
                      }
                      twelveHour
                    />
                    <TimePicker
                      required
                      label="End Time"
                      value={
                        row.latestMeetingEndTime
                          ? new Date(row.latestMeetingEndTime)
                          : null
                      }
                      onChange={(d) =>
                        updateScheduleRow(index, 'latestMeetingEndTime', d)
                      }
                      errorMessage={
                        touched && isEmpty(row.latestMeetingEndTime)
                          ? 'Required'
                          : null
                      }
                      twelveHour
                    />
                    <div className="flex gap-2 items-end">
                      <Button
                        className="w-8 p-0 justify-center"
                        variant="outline"
                        onClick={addScheduleRow}
                      >
                        +
                      </Button>
                      {meetingPeriods.length > 0 && (
                        <Button
                          className="w-8 p-0 justify-center"
                          variant="outline"
                          onClick={() => removeScheduleRow(index)}
                        >
                          -
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
          <Input
            className="col-start-1"
            label="Venue"
            value={event.venue}
            onChange={(e) => handleChange('venue', e.target.value)}
          />
          <Input
            className="col-start-1"
            label="Street Address"
            value={event.address1}
            onChange={(e) => handleChange('address1', e.target.value)}
          />
          <Input
            label="Address 2"
            value={event.address2}
            onChange={(e) => handleChange('address2', e.target.value)}
          />
          <Input
            label="Address 3"
            value={event.address3}
            onChange={(e) => handleChange('address3', e.target.value)}
          />
          <Select.AutoComplete
            className="col-start-1"
            label="Country"
            placeholder="Select country"
            value={event.country}
            onChange={(e) => handleChange('country', e)}
          >
            {({ inputValue }) =>
              inputValue.length > 0
                ? map(
                    countries.filter((a) =>
                      a.name.toLowerCase().includes(inputValue.toLowerCase())
                    ),
                    (c) => (
                      <Select.Option value={c.name}>{c.name}</Select.Option>
                    )
                  )
                : 'Begin typing to search'
            }
          </Select.AutoComplete>
          <Input
            label="City"
            value={event.city}
            onChange={(e) => handleChange('city', e.target.value)}
          />
          <Input
            label="State/Province"
            value={event.state}
            onChange={(e) => handleChange('state', e.target.value)}
          />
          <Input
            label="Zipcode"
            value={event.state}
            onChange={(e) => handleChange('postalCode', e.target.value)}
          />
        </div>
        <div className="mt-8 flex justify-end">
          <Button iconLeft={<Save />} onClick={handleSave} variant="filled">
            Save
          </Button>
        </div>
      </div>
    </>
  )
}

export default NewEventForm
